import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { LucideIcon } from "@/components/icons"
import type { OnboardingStep } from "@/types/user-settings"

import { Check } from "@/components/icons"
import { LoadingSection } from "@/components/loading-section"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { NodePill } from "@/components/ui/node-pill"

export function OnboardingModalShell({
  children,
  currentStep,
}: {
  children: ReactNode
  currentStep: OnboardingStep
}) {
  const { t } = useTranslation()
  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        hideCloseButton
        className="z-[81] max-h-[calc(100dvh-2rem)] gap-0 overflow-hidden p-0 sm:max-w-4xl"
        data-testid="onboarding-modal"
        overlayProps={{ className: "z-[80] bg-black/80 backdrop-blur-[2px]" }}
        overlayTestId="onboarding-backdrop"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t(`personalization.smoothOnboarding.steps.${currentStep}`)}</DialogTitle>
          <DialogDescription>
            {t("personalization.smoothOnboarding.welcome.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 w-full flex-col gap-6 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function OnboardingProgressHeader({
  currentStep,
  steps,
}: {
  currentStep: OnboardingStep
  steps: OnboardingStep[]
}) {
  const { t } = useTranslation()
  const currentIndex = steps.indexOf(currentStep)

  // Micrographics "flow" form: the steps are pills joined by 3X connector
  // lines, with the current step filled blue (charcoal text) and completed
  // steps carrying the check in a secondary tone.
  return (
    <nav aria-label={t("personalization.smoothOnboarding.progressLabel")}>
      <ol className="mx-auto flex w-full max-w-2xl flex-wrap items-center justify-center gap-y-2">
        {steps.map((step, index) => {
          const complete = index < currentIndex
          const current = index === currentIndex
          return (
            <li key={step} className="flex items-center">
              <NodePill
                active={current}
                aria-current={current ? "step" : undefined}
                connector={index < steps.length - 1 ? "right" : "none"}
                icon={complete ? Check : undefined}
                kind="flow"
                size="sm"
                tone={complete ? "secondary" : "outlined"}
              >
                {t(`personalization.smoothOnboarding.steps.${step}`)}
              </NodePill>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export function OnboardingWelcomeCard({
  description,
  icon: Icon,
}: {
  description: string
  icon: LucideIcon
}) {
  return (
    <div className="border-border bg-background flex items-start gap-3 rounded-lg border p-4">
      <Icon aria-hidden="true" className="text-primary mt-0.5 size-5 shrink-0" />
      <p className="text-sm leading-relaxed">{description}</p>
    </div>
  )
}

export function OnboardingProfileRow({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) {
  const { t } = useTranslation()
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value || t("personalization.onboarding.role.notSet")}</span>
    </div>
  )
}

export function OnboardingCheckOption({
  checked,
  id,
  label,
  onChange,
}: {
  checked: boolean
  id: string
  label: string
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex min-h-10 items-center gap-2">
      <Checkbox checked={checked} id={id} onCheckedChange={(value) => onChange(value === true)} />
      <Label className="cursor-pointer" htmlFor={id}>
        {label}
      </Label>
    </div>
  )
}

export function OnboardingForceResetStatus({
  error,
  onRetry,
}: {
  error: string | null
  onRetry: () => void
}) {
  const { t } = useTranslation()
  if (!error) {
    return (
      <main className="flex min-h-svh items-center justify-center">
        <LoadingSection />
      </main>
    )
  }
  return (
    <main className="bg-background flex min-h-svh items-center justify-center p-6">
      <section className="border-border bg-card w-full max-w-md rounded-xl border p-6 text-center shadow-sm">
        <h1 className="font-display text-2xl">
          {t("personalization.smoothOnboarding.loadErrorTitle")}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">{error}</p>
        <Button className="mt-6 min-h-10" onClick={onRetry}>
          {t("personalization.smoothOnboarding.retry")}
        </Button>
      </section>
    </main>
  )
}
